import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SnackbarKey, useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import {
  AnkiImportSuccessfulMapping,
  getAnkiImportStatus,
} from '../services/anki';
import { RootState } from '../store/store';
import {
  AnkiImportNotifierContext,
  AnkiImportNotifierContextValue,
} from './AnkiImportNotifierContext';
import { CheckCircle, X } from '@phosphor-icons/react';
import { capture } from '../utils/analytics';

const AnkiImportAnimation = lazy(
  () => import('../components/ora/FlashcardDecks/AnkiImportAnimation')
);

interface AnkiImportNotifierProviderProps {
  children: React.ReactNode;
}

interface AnkiImportStatusJob {
  id: string;
  status: string;
  error?: string | null;
  totalCards?: number;
  cardsWithScheduling?: number;
  cardsMapped?: number;
  cardsScheduled?: number;
  cardsSkipped?: number;
  cardsDuplicates?: number;
  cardsErrored?: number;
  cardsDeferred?: number;
  successfulMappings?: AnkiImportSuccessfulMapping[] | null;
}

interface DeferredNotification {
  message: string;
  variant: 'success' | 'error';
  jobId: string;
  status: 'COMPLETED' | 'FAILED';
  totalCards: number;
  cardsWithScheduling: number;
  cardsMapped: number;
  cardsSkipped: number;
  cardsDuplicates: number;
  cardsDeferred: number;
  successfulMappings: AnkiImportSuccessfulMapping[];
}

const POLL_INTERVAL_MS = 5000;
const FINAL_STATUSES = new Set(['COMPLETED', 'FAILED']);
const ACTIVE_STATUSES = new Set(['PENDING', 'PROCESSING']);
const notifiedJobIds = new Set<string>();

const toUpperStatus = (status: string | undefined) =>
  String(status || '').toUpperCase();

const buildCompletedMessage = (job: AnkiImportStatusJob) => {
  const mapped = job.cardsMapped ?? 0;
  const skipped = job.cardsSkipped ?? 0;
  const duplicates = job.cardsDuplicates ?? 0;
  const errored = job.cardsErrored ?? 0;
  const deferred = job.cardsDeferred ?? 0;
  const base = `Anki import complete: ${mapped} mapped, ${skipped} skipped, ${duplicates} duplicates, ${errored} errors.`;
  return deferred > 0
    ? `${base} ${deferred} new cards still to import — re-import the same deck to finish.`
    : base;
};

const buildFailureMessage = (job: AnkiImportStatusJob) =>
  job.error ? `Anki import failed: ${job.error}` : 'Anki import failed.';

const extractFailureReason = (message: string) =>
  message.replace(/^anki import failed:\s*/i, '').trim() || 'Import failed';

interface AnimationPayload {
  jobId: string;
  status: 'COMPLETED' | 'FAILED';
  totalCards: number;
  cardsWithScheduling: number;
  cardsMapped: number;
  cardsDuplicates: number;
  cardsDeferred: number;
  successfulMappings: AnkiImportSuccessfulMapping[];
}

const toSuccessfulMappings = (
  value: AnkiImportSuccessfulMapping[] | null | undefined
) => (Array.isArray(value) ? value.slice(0, 100) : []);

const AnkiImportNotifierProvider: React.FC<AnkiImportNotifierProviderProps> = ({
  children,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const trackedJobIdsRef = useRef<Set<string>>(new Set());
  const lastSeenStatusByJobIdRef = useRef<Map<string, string>>(new Map());
  const [isAnimationVisible, setIsAnimationVisible] = useState(false);

  const [deferredNotification, setDeferredNotification] =
    useState<DeferredNotification | null>(null);

  const [animationPayload, setAnimationPayload] =
    useState<AnimationPayload | null>(null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const openCompletionAnimation = useCallback((payload: AnimationPayload) => {
    if (payload.status !== 'COMPLETED') return;
    setAnimationPayload(payload);
  }, []);

  const showNotification = useCallback(
    (notification: DeferredNotification) => {
      const snackbarKeyRef: { current: SnackbarKey | null } = {
        current: null,
      };

      const handleOpenToastAnimation = () => {
        if (snackbarKeyRef.current) {
          closeSnackbar(snackbarKeyRef.current);
        }

        openCompletionAnimation({
          jobId: notification.jobId,
          status: notification.status,
          totalCards: notification.totalCards,
          cardsWithScheduling: notification.cardsWithScheduling,
          cardsMapped: notification.cardsMapped,
          cardsDuplicates: notification.cardsDuplicates,
          cardsDeferred: notification.cardsDeferred,
          successfulMappings: notification.successfulMappings,
        });
      };

      const message = (
        <div
          className="anki-import-notification"
          role={notification.status === 'COMPLETED' ? 'button' : undefined}
          tabIndex={notification.status === 'COMPLETED' ? 0 : undefined}
          onClick={
            notification.status === 'COMPLETED'
              ? handleOpenToastAnimation
              : undefined
          }
          onKeyDown={(event) => {
            if (notification.status !== 'COMPLETED') return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpenToastAnimation();
            }
          }}
        >
          <div className="anki-import-notification_message">
            <CheckCircle weight="fill" />
            {notification.status === 'COMPLETED'
              ? 'Anki Import Complete'
              : 'Anki Import Failed' +
                ' - ' +
                extractFailureReason(notification.message)}
          </div>
          {notification.status === 'COMPLETED' ? (
            <div className="anki-import-notification_actions">
              <button
                type="button"
                className="anki-import-notification_view"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenToastAnimation();
                }}
              >
                View
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="button button--icon-only button--dismiss"
              title={extractFailureReason(notification.message)}
              aria-label={`Dismiss failed import notification. Reason: ${extractFailureReason(notification.message)}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (snackbarKeyRef.current) {
                  closeSnackbar(snackbarKeyRef.current);
                }
              }}
            >
              <X />
            </button>
          )}
        </div>
      );

      snackbarKeyRef.current = enqueueSnackbar(message, {
        variant: notification.variant,
        persist: true,
      });
    },
    [closeSnackbar, enqueueSnackbar, openCompletionAnimation]
  );

  const deliverOrDeferNotification = useCallback(
    (notification: DeferredNotification) => {
      if (isAnimationVisible) {
        setDeferredNotification(notification);
        return;
      }
      showNotification(notification);
    },
    [isAnimationVisible, showNotification]
  );

  const [isPollingActive, setIsPollingActive] = useState(false);

  const pollAnkiImportStatus = useCallback(async () => {
    if (!currentUser?.id) return;

    try {
      const response = await getAnkiImportStatus();
      const job = response?.data as AnkiImportStatusJob | null | undefined;
      if (!job?.id) {
        setIsPollingActive(false);
        return;
      }

      const status = toUpperStatus(job.status);

      const previousStatus = toUpperStatus(
        lastSeenStatusByJobIdRef.current.get(job.id)
      );

      if (ACTIVE_STATUSES.has(status)) {
        trackedJobIdsRef.current.add(job.id);
        setIsPollingActive(true);
      }

      const isTracked = trackedJobIdsRef.current.has(job.id);
      const isFinalStatus = FINAL_STATUSES.has(status);

      const transitionedFromActive =
        ACTIVE_STATUSES.has(previousStatus) && isFinalStatus;

      if (
        isFinalStatus &&
        isTracked &&
        !notifiedJobIds.has(job.id) &&
        (transitionedFromActive || !previousStatus)
      ) {
        const completedOrFailedStatus =
          status === 'COMPLETED' ? 'COMPLETED' : 'FAILED';

        const message =
          completedOrFailedStatus === 'COMPLETED'
            ? buildCompletedMessage(job)
            : buildFailureMessage(job);

        const analyticsProps = {
          jobId: job.id,
          totalCards: job.totalCards ?? 0,
          cardsWithScheduling: job.cardsWithScheduling ?? 0,
          cardsMapped: job.cardsMapped ?? 0,
          cardsScheduled: job.cardsScheduled ?? 0,
          cardsSkipped: job.cardsSkipped ?? 0,
          cardsDuplicates: job.cardsDuplicates ?? 0,
          cardsErrored: job.cardsErrored ?? 0,
          cardsDeferred: job.cardsDeferred ?? 0,
        };

        if (completedOrFailedStatus === 'COMPLETED') {
          capture('anki_import_completed', analyticsProps);
        } else {
          capture('anki_import_failed', {
            ...analyticsProps,
            failureReason: extractFailureReason(message),
          });
        }

        notifiedJobIds.add(job.id);

        deliverOrDeferNotification({
          variant:
            completedOrFailedStatus === 'COMPLETED' ? 'success' : 'error',
          status: completedOrFailedStatus,
          jobId: job.id,
          totalCards: analyticsProps.totalCards,
          cardsWithScheduling: analyticsProps.cardsWithScheduling,
          cardsMapped: analyticsProps.cardsMapped,
          cardsSkipped: analyticsProps.cardsSkipped,
          cardsDuplicates: analyticsProps.cardsDuplicates,
          cardsDeferred: analyticsProps.cardsDeferred,
          successfulMappings: toSuccessfulMappings(job.successfulMappings),
          message,
        });
      }

      if (isFinalStatus) {
        setIsPollingActive(false);
      }

      lastSeenStatusByJobIdRef.current.set(job.id, status);
    } catch {
      // Keep notifier silent for transient polling failures.
    }
  }, [currentUser?.id, deliverOrDeferNotification]);

  const pollFnRef = useRef(pollAnkiImportStatus);
  pollFnRef.current = pollAnkiImportStatus;

  // One-shot check on mount to detect active jobs from before page load
  useEffect(() => {
    if (!currentUser?.id) return;
    void pollFnRef.current();
  }, [currentUser?.id]);

  // Polling interval — only runs while an active job exists
  useEffect(() => {
    if (!currentUser?.id || !isPollingActive) return;

    const intervalId = window.setInterval(() => {
      void pollAnkiImportStatus();
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [currentUser?.id, isPollingActive, pollAnkiImportStatus]);

  useEffect(() => {
    if (!isAnimationVisible && deferredNotification) {
      showNotification(deferredNotification);
      setDeferredNotification(null);
    }
  }, [deferredNotification, isAnimationVisible, showNotification]);

  const value = useMemo<AnkiImportNotifierContextValue>(
    () => ({
      registerStartedJob: (jobId: string) => {
        if (!jobId) return;
        trackedJobIdsRef.current.add(jobId);
        setIsPollingActive(true);
      },
      setAnimationVisible: (visible: boolean) => {
        setIsAnimationVisible(visible);
      },
      isImportInProgress: isPollingActive,
    }),
    [isPollingActive]
  );

  return (
    <AnkiImportNotifierContext.Provider value={value}>
      {children}
      {animationPayload?.status === 'COMPLETED' && (
        <Suspense fallback={null}>
          <AnkiImportAnimation
            jobId={animationPayload.jobId}
            status={animationPayload.status}
            totalCards={animationPayload.totalCards}
            cardsWithScheduling={animationPayload.cardsWithScheduling}
            cardsMapped={animationPayload.cardsMapped}
            cardsDuplicates={animationPayload.cardsDuplicates}
            cardsDeferred={animationPayload.cardsDeferred}
            successfulMappings={animationPayload.successfulMappings}
            onDismiss={() => setAnimationPayload(null)}
          />
        </Suspense>
      )}
    </AnkiImportNotifierContext.Provider>
  );
};

export default AnkiImportNotifierProvider;
